// pages/Post.js
import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import MediaGrid from "../components/MediaGrid";
import PostInputComposer from "../components/PostInputComposer";
import { CommentReply } from "../components/Comment";
import {
  IconComment,
  IconLike,
  IconViews,
  IconShare,
  IconRepost,
  IconMore,
} from "../components/FeedIcons";
import CommentCard from "../cards/CommentCard";
import PostViewCard from "../cards/PostViewCard";
import useDesktopComposer from "../hooks/useDesktopComposer";
import useMobileComposer from "../hooks/useMobileComposer";
import useBottomSheet from "../hooks/useBottomSheet";
import UserInfoOverlay from "../components/UserInfoOverlay";


export default function Post({
  myChatId,
  myUserName,
    myDisplayName,
  users,
  feedPosts,
  onLikePost,
  onLikeComment,
  onRepostComment,
  onViewComment,
  onRepost,
  avatarUrl,
  onAddComment,
  comments,
  feedActionSocket,
  feedSocket,
  // 🆕 comment pagination
  onLoadMoreComments,
  commentHasMore,
  commentLoading,
  uswdWallet,
   uploadFeedMedia,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const initialPost = location.state?.post;
  const autoFocusHandledRef = useRef(false);

  const [post, setPost] = useState(initialPost);
  const [loading, setLoading] = useState(false);

  const [isReplyingToPost, setIsReplyingToPost] = useState(false);
  const [postReplyText, setPostReplyText] = useState("");
  const [repostMenu, setRepostMenu] = useState({ open: false, post: null });

  const [commentReplyMediaFiles, setCommentReplyMediaFiles] = useState([]);
  const [commentReplyMediaPreview, setCommentReplyMediaPreview] = useState([]);

  const [commentRepostMenu, setCommentRepostMenu] = useState({ open: false, comment: null });
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState("");

  const previewCommentId = location.state?.previewCommentId || null;

  const [menu, setMenu] = useState({ open: false, post: null, type: null });
  const [selectedUserId, setSelectedUserId] = useState(null);

  // ── SUPPORT MODAL STATE (works for both posts and comments) ──────────────
  // target: { kind: "post" | "comment", data: <post|comment object> }
  const [supportModal, setSupportModal] = useState({ open: false, target: null });
  const [supportAmount, setSupportAmount] = useState("");
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportError, setSupportError] = useState("");
  const [supportResult, setSupportResult] = useState(null);

  // ── EARNINGS MODAL STATE (author view, works for both posts and comments) ─
  const [earningsModal, setEarningsModal] = useState({ open: false, target: null });

  const NETWORK_FEE_USWD = 0.01;

  const [showComposer, setShowComposer] = useState(false);
  const [composerText, setComposerText] = useState("");
  const [quotePost, setQuotePost] = useState(null);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaPreview, setMediaPreview] = useState([]);
  const [isReplyMode, setIsReplyMode] = useState(false);
  const [replyToPost, setReplyToPost] = useState(null);

  const [replyingCommentId, setReplyingCommentId] = useState(null);
  const [commentReplyText, setCommentReplyText] = useState("");
  const [quoteTarget, setQuoteTarget] = useState(null);
  const pendingScrollRef = useRef(null);
  const [scrollTick, setScrollTick] = useState(0);
  const scrollContainerRef = useRef(null);
  const [headerVisible, setHeaderVisible] = useState(true);
  const lastScrollYRef = useRef(0);
  const headerHideThreshold = 20;
  const [highlightId, setHighlightId] = useState(null);

  /* ── AUTO COMMENT PAGINATION + HEADER HIDE/SHOW ON SCROLL ── */
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const currentY = container.scrollTop;

      const delta = currentY - lastScrollYRef.current;
      if (Math.abs(delta) >= headerHideThreshold) {
        setHeaderVisible(delta < 0 || currentY < 10);
        lastScrollYRef.current = currentY;
      }

      if (!commentHasMore || commentLoading) return;

      const nearBottom =
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - 500;

      if (!nearBottom) return;

      onLoadMoreComments?.(post?.postId);
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [post?.postId, commentHasMore, commentLoading, onLoadMoreComments]);

  const resolveUserAvatar = (
  chatId,
  fallback = null
) => {

  if (
    String(chatId) ===
    String(myChatId)
  ) {
    return avatarUrl;
  }

  return (
    fallback ||
    users?.find(
      (u) =>
        String(u.chatId) ===
        String(chatId)
    )?.profileImageUrl ||
    null
  );
};

  const closeAllReplies = () => {
    setIsReplyingToPost(false);
    setReplyingCommentId(null);
    setPostReplyText("");
    setCommentReplyText("");
    setQuoteTarget(null);
  };

  useEffect(() => {
    if (!pendingScrollRef.current) return;
    const { id, duration } = pendingScrollRef.current;
    const container = scrollContainerRef.current;
    if (!container) return;
    let attempts = 0;
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (!el) {
        if (attempts++ < 20) requestAnimationFrame(tryScroll);
        return;
      }
      const containerTop = container.getBoundingClientRect().top;
      const elTop = el.getBoundingClientRect().top;
      container.scrollTo({
        top: container.scrollTop + (elTop - containerTop) - 90,
        behavior: "smooth",
      });
      setTimeout(() => {
        el.classList.add("ring-2", "ring-blue-500", "ring-offset-2", "transition-all", "duration-500");
        setTimeout(() => el.classList.remove("ring-2", "ring-blue-500", "ring-offset-2"), duration);
      }, 450);
      pendingScrollRef.current = null;
    };
    tryScroll();
  }, [comments, post?.postId, scrollTick]);

  useEffect(() => {
    if (!highlightId) return;
    const t = setTimeout(() => setHighlightId(null), 4000);
    return () => clearTimeout(t);
  }, [highlightId]);

  const handleCommentReplyMediaSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setCommentReplyMediaFiles(files);
    const previews = files.map((file) => ({
      type: file.type.startsWith("video") ? "video" : "image",
      url: URL.createObjectURL(file),
    }));
    setCommentReplyMediaPreview(previews);
  };

  const handleQuoteClick = (quote) => {
    if (!quote?.type || !quote?.id) return;
    const targetId =
      quote.type === "post" ? `post-card-${quote.id}` : `comment-card-${quote.id}`;
    pendingScrollRef.current = { id: targetId, duration: 4000 };
    setHighlightId(targetId);
    setScrollTick((n) => n + 1);
    if (quote.type === "post" && post?.postId !== quote.id) {
      navigate(".", {
        replace: true,
        state: { view: "post", post: { postId: quote.id } },
      });
    }
  };

  const isPureRepost = post?.isRepost && !post?.isQuote && post?.originalPost;
  const displayPost = isPureRepost ? post.originalPost : post;
  const isPostDeleted = displayPost?.isDeleted;
  const authorAvatar =
  resolveUserAvatar(
    displayPost.author?.chatId,
    displayPost.author?.profileImageUrl
  );

  useEffect(() => {
    if (!post || !feedPosts?.length) return;
    const live = feedPosts.find((p) => String(p.postId) === String(post.postId));
    if (live) {
      setPost((prev) => ({
        ...prev,
        ...live,
        originalPost:
          String(live.postId) === String(prev?.originalPost?.postId)
            ? live
            : live.originalPost || prev.originalPost,
      }));
    }
  }, [feedPosts, post?.postId, post?.originalPost?.postId]);

  const lastHandledIntentRef = useRef(null);

  useEffect(() => {
    const intentId = location.state?.replyIntentId;
    if (!intentId) return;
    if (lastHandledIntentRef.current === intentId) return;
    lastHandledIntentRef.current = intentId;
    setIsReplyingToPost(true);
    if (location.state?.quoteTarget) setQuoteTarget(location.state.quoteTarget);
  }, [location.state?.replyIntentId]);

  /* ── COMMENT TREE ── */
  const commentTree = useMemo(() => {
    const filtered = comments.filter((c) => !c.isRepost);
    const map = {};
    const roots = [];
    filtered.forEach((c) => { map[c.commentId] = { ...c, children: [] }; });
    filtered.forEach((c) => {
      if (c.parentCommentId && map[c.parentCommentId]) {
        map[c.parentCommentId].children.push(map[c.commentId]);
      } else {
        roots.push(map[c.commentId]);
      }
    });
    roots.forEach((root) => {
      root.children.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    });
    roots.sort((a, b) => {
      const aLatest = a.children.length
        ? Math.max(new Date(a.createdAt).getTime(), ...a.children.map((c) => new Date(c.createdAt).getTime()))
        : new Date(a.createdAt).getTime();
      const bLatest = b.children.length
        ? Math.max(new Date(b.createdAt).getTime(), ...b.children.map((c) => new Date(c.createdAt).getTime()))
        : new Date(b.createdAt).getTime();
      return bLatest - aLatest;
    });
    return roots;
  }, [comments]);

  const autoExpandIds = useMemo(() => {
    if (!previewCommentId || !comments?.length) return new Set();
    const map = {};
    comments.forEach((c) => { map[c.commentId] = c; });
    const expandSet = new Set();
    let current = map[previewCommentId];
    while (current?.parentCommentId) {
      expandSet.add(current.parentCommentId);
      current = map[current.parentCommentId];
    }
    return expandSet;
  }, [previewCommentId, comments]);

  /* ── COMPOSERS (must be called before any return) ── */
  const desktopComposer = useDesktopComposer({
    showComposer,
    setShowComposer,
    text: composerText,
    setText: setComposerText,
    mediaFiles,
    mediaPreview,
    isPosting: false,
    handleMediaSelect: (e) => {
      const files = Array.from(e.target.files || []);
      setMediaFiles(files);
      setMediaPreview(files.map((file) => ({
        type: file.type.startsWith("video") ? "video" : "image",
        url: URL.createObjectURL(file),
      })));
    },
  handlePost: () => {
  if (!composerText.trim() && !quotePost) return;
  if (quotePost?.type === "comment") {
    onRepostComment({
      commentId: quotePost.commentId,
      chatId: myChatId,
      text: composerText,
      originalComment: quotePost,
      authorUsername: myUserName,        // ✅ NEW
      authorDisplayName: myDisplayName,  // ✅ NEW
    });
  } else if (quotePost?.type === "post") {
    onRepost({
      postId: quotePost.postId,
      chatId: myChatId,
      originalPost: quotePost,
      text: composerText,
      authorUsername: myUserName,        // ✅ NEW
      authorDisplayName: myDisplayName,  // ✅ NEW
    });
  }
  setComposerText("");
  setQuotePost(null);
  setShowComposer(false);
},
    handleSendComment: () => {},
    isReplyMode,
    replyToPost,
    quotePost,
    setQuotePost,
    setMediaFiles,
    setMediaPreview,
    setIsReplyMode,
    setReplyToPost,
  });

  const mobileComposer = useMobileComposer({
    showComposer,
    setShowComposer,
    avatarUrl,
    text: composerText,
    setText: setComposerText,
    mediaFiles,
    mediaPreview,
    uploadProgress: {},
    isPosting: false,
    handleMediaSelect: (e) => {
      const files = Array.from(e.target.files || []);
      setMediaFiles(files);
      setMediaPreview(files.map((file) => ({
        type: file.type.startsWith("video") ? "video" : "image",
        url: URL.createObjectURL(file),
      })));
    },
   handlePost: () => {
  if (!composerText.trim() && !quotePost) return;
  onRepostComment({
    commentId: quotePost?.commentId,
    chatId: myChatId,
    text: composerText,
    originalComment: quotePost,
    authorUsername: myUserName,        // ✅ NEW
    authorDisplayName: myDisplayName,  // ✅ NEW
  });
  setComposerText("");
  setQuotePost(null);
  setShowComposer(false);
},
    quotePost,
    setQuotePost,
    setMediaFiles,
    setMediaPreview,
    setIsReplyMode,
    setReplyToPost,
  });

const bottomSheet = useBottomSheet({
  menu,
  setMenu,
  users,
  myChatId,
  myUserName,          // ✅ NEW
  myDisplayName,       // ✅ NEW
  onRepost,
  onDeletePost: ({ postId, commentId }) => {
    if (commentId) {
      feedActionSocket?.deleteComment({ commentId, postId: post.postId });
    } else if (postId) {
      feedActionSocket?.deletePost({ postId });
    }
  },
  onEditPost: (target) => {
    if (menu.type === "comment-actions") {
      setEditingCommentId(target.commentId);
      setEditingCommentText(target.text || "");
      return;
    }
    setComposerText(target.text || "");
    setShowComposer(true);
  },
  onReportPost: ({ postId, reason }) => {
    feedActionSocket?.reportPost?.({ postId, reason });
  },
  followUser: feedSocket?.followUser,
  unfollowUser: feedSocket?.unfollowUser,
  setQuotePost,
  setShowComposer,
  setIsReplyMode,
  setReplyToPost,
});
  // ── SUPPORT MODAL HANDLERS ───────────────────────────────────────────────

 const handleOpenSupportModal = useCallback(({ post: targetPost }) => {
    setSupportModal({ open: true, target: { kind: "post", data: targetPost } });
    setSupportAmount("");
    setSupportError("");
    setSupportResult(null); // NEW
  }, []);

  const handleOpenCommentSupportModal = useCallback(({ comment }) => {
    setSupportModal({ open: true, target: { kind: "comment", data: comment } });
    setSupportAmount("");
    setSupportError("");
    setSupportResult(null); // NEW
  }, []);

  const handleCloseSupportModal = useCallback(() => {
    if (supportLoading) return;
    setSupportModal({ open: false, target: null });
    setSupportAmount("");
    setSupportError("");
    setSupportResult(null); // NEW
  }, [supportLoading]);

  // ── SUPPORT RESULT LISTENERS ──────────────────────────────────────────────
  useEffect(() => {
    if (!feedSocket?.onSupportPostResult) return;
    const unsubscribe = feedSocket.onSupportPostResult((result) => {
      setSupportLoading(false);
      if (!result?.success) {
        toast?.error?.(result?.error || "Failed to send support");
        setSupportError(result?.error || "Failed to send support");
        return;
      }
      setSupportResult(result.data); // { amount, fee, total, ... }
    });
    return unsubscribe;
  }, [feedSocket]);

  useEffect(() => {
    if (!feedSocket?.onSupportCommentResult) return;
    const unsubscribe = feedSocket.onSupportCommentResult((result) => {
      setSupportLoading(false);
      if (!result?.success) {
        toast?.error?.(result?.error || "Failed to send support");
        setSupportError(result?.error || "Failed to send support");
        return;
      }
      setSupportResult(result.data); // { amount, fee, total, ... }
    });
    return unsubscribe;
  }, [feedSocket]);
 
    const handleSupportSubmit = async () => {
    const amount = parseFloat(supportAmount);

    if (!amount || amount <= 0) {
      setSupportError("Enter a valid amount");
      return;
    }

    const target = supportModal.target;
    if (!target?.data) {
      setSupportError("Not available");
      return;
    }

    setSupportLoading(true);
    setSupportError("");
    setSupportResult(null); // NEW

    try {
      const timestamp = Date.now();

      const isComment = target.kind === "comment";

      if (isComment) {
        feedSocket?.supportComment?.({
          commentId: target.data.commentId,
          amount,
          timestamp,
        });
      } else {
        feedSocket?.supportPost?.({
          postId: target.data.postId,
          amount,
          timestamp,
        });
      }
      // NOTE: modal stays open (loading) until result event fires
    } catch (err) {
      setSupportError(err?.message || "Failed to send support");
      setSupportLoading(false); // NEW
    }
  };
  // ── EARNINGS MODAL HANDLERS (author view) ────────────────────────────────

  const handleOpenEarningsModal = useCallback(({ post: targetPost }) => {
    setEarningsModal({ open: true, target: { kind: "post", data: targetPost } });
  }, []);

  const handleOpenCommentEarningsModal = useCallback(({ comment }) => {
    setEarningsModal({ open: true, target: { kind: "comment", data: comment } });
  }, []);

  const handleCloseEarningsModal = useCallback(() => {
    setEarningsModal({ open: false, target: null });
  }, []);

  if (loading) {
    return <div className="p-6 text-center text-neutral-500">Loading post…</div>;
  }

  if (!post) {
    return <div className="p-6 text-center text-neutral-500">Post not found</div>;
  }

  /* ── REPOST SHEET SHARED JSX ── */
  const RepostSheetButtons = ({ target, onRepost: doRepost, onQuote, onClose }) => (
    <>
      <div className="flex justify-center py-2">
        <div className="w-10 h-1.5 rounded-full bg-neutral-300 dark:bg-neutral-700" />
      </div>
      <button
        onClick={doRepost}
        className="w-full px-4 py-3.5 text-left hover:bg-neutral-100 dark:hover:bg-neutral-800 flex items-center gap-4 text-base font-medium touch-manipulation"
        style={{ minHeight: 52 }}
      >
        🔁 Repost
      </button>
      <button
        onClick={onQuote}
        className="w-full px-4 py-3.5 text-left hover:bg-neutral-100 dark:hover:bg-neutral-800 flex items-center gap-4 text-base font-medium touch-manipulation"
        style={{ minHeight: 52 }}
      >
        ✏️ Quote
      </button>
      <button
        onClick={onClose}
        className="w-full px-4 py-3.5 text-left text-red-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 font-medium touch-manipulation"
        style={{ minHeight: 52 }}
      >
        Cancel
      </button>
      {/* iOS home indicator spacer */}
      <div style={{ height: "env(safe-area-inset-bottom, 0px)" }} aria-hidden="true" />
    </>
  );

  // ── Derive display text/amount for the support / earnings modals ─────────
  const supportTarget = supportModal.target;
  const supportTargetText = supportTarget
    ? supportTarget.kind === "comment"
      ? supportTarget.data.text
      : supportTarget.data.text
    : null;
  const supportTargetAuthor = supportTarget?.data?.author;

  const earningsTarget = earningsModal.target;
  const earningsTargetText = earningsTarget?.data?.text;
  const earningsAmount =
    earningsTarget?.data?.supportsAmount ?? earningsTarget?.data?.supportReceived ?? 0;

  return (
    <div
      ref={scrollContainerRef}
      className="flex justify-center w-full h-full overflow-y-auto"
      style={{ WebkitOverflowScrolling: "touch" }}
    >
      <div className="w-full max-w-[640px] border-x border-neutral-200 dark:border-neutral-700">
      <PostViewCard
  post={post}
  displayPost={{
    ...displayPost,
    text: isPostDeleted ? "This post was deleted" : displayPost?.text,
  }}
  isDeleted={isPostDeleted}
  isEdited={displayPost?.isEdited}
  highlightId={highlightId}
  isPureRepost={isPureRepost}
  authorAvatar={authorAvatar}
  headerVisible={headerVisible}
  navigate={navigate}
  closeAllReplies={closeAllReplies}
  setQuoteTarget={setQuoteTarget}
  setIsReplyingToPost={setIsReplyingToPost}
  setRepostMenu={setRepostMenu}
  setMenu={setMenu}
  onLikePost={onLikePost}
  myChatId={myChatId}
  quoteTarget={quoteTarget}
  users={users}
  onSupportPost={handleOpenSupportModal}
  onViewEarnings={handleOpenEarningsModal}
  onOpenUser={(chatId) => setSelectedUserId(chatId)}
/>

        {/* REPLY TO POST */}
        {isReplyingToPost && (
          <PostInputComposer
            avatarUrl={avatarUrl}
            value={postReplyText}
            onChange={setPostReplyText}
            autoFocus
            showCancel
            inlineActions
            showMedia
            mediaFiles={mediaFiles}
            mediaPreview={mediaPreview}
            onSelectMedia={(e) => {
              const files = Array.from(e.target.files || []);
              setMediaFiles(files);
              setMediaPreview(files.map((file) => ({
                type: file.type.startsWith("video") ? "video" : "image",
                url: URL.createObjectURL(file),
              })));
            }}
            placeholder="Write a reply"
            submitLabel="Reply"
            onCancel={() => { closeAllReplies(); setMediaFiles([]); setMediaPreview([]); }}
           onSubmit={async () => {
              if (!postReplyText.trim() && mediaFiles.length === 0) return;
              let uploadedMedia = [];
              if (mediaFiles.length > 0 && uploadFeedMedia) {
                try {
                  uploadedMedia = await uploadFeedMedia(mediaFiles, () => {});
                } catch (err) {
                  toast?.error?.("Failed to upload media");
                  return;
                }
              }
              onAddComment({
                postId: post.postId,
                chatId: myChatId,
                authorUsername: myUserName,
                  authorDisplayName: myDisplayName,
                text: postReplyText,
                parentCommentId: null,
                quote: quoteTarget,
                media: uploadedMedia,
              });
              setPostReplyText("");
              setMediaFiles([]);
              setMediaPreview([]);
              setIsReplyingToPost(false);
              setQuoteTarget(null);
            }}
          />
        )}

        {/* COMMENTS */}
        <div>
          {commentTree.map((c) => (
            <React.Fragment key={c.commentId}>
              <CommentCard
                item={c}
                isDeleted={c.isDeleted}
                isEdited={c.isEdited}
                setMenu={setMenu}
                autoExpandIds={autoExpandIds}
                myChatId={myChatId}
                resolveUserAvatar={resolveUserAvatar}
                highlightId={highlightId}
                handleQuoteClick={handleQuoteClick}
                closeAllReplies={closeAllReplies}
                commentReplyMediaFiles={commentReplyMediaFiles}
                commentReplyMediaPreview={commentReplyMediaPreview}
                setCommentReplyMediaFiles={setCommentReplyMediaFiles}
                setCommentReplyMediaPreview={setCommentReplyMediaPreview}
                onSelectReplyMedia={handleCommentReplyMediaSelect}
               setReplyingCommentId={(id) => {
                  closeAllReplies();
                  setReplyingCommentId(id);
                }}
                onLikeComment={onLikeComment}
                onOpenRepostMenu={(comment) => setCommentRepostMenu({ open: true, comment })}
                onViewComment={onViewComment}
                replyingCommentId={replyingCommentId}
                commentReplyText={commentReplyText}
                setCommentReplyText={setCommentReplyText}
               onSubmitReply={async (parentId) => {
                  let uploadedMedia = [];
                  if (commentReplyMediaFiles.length > 0 && uploadFeedMedia) {
                    try {
                      uploadedMedia = await uploadFeedMedia(commentReplyMediaFiles, () => {});
                    } catch (err) {
                      toast?.error?.("Failed to upload media");
                      return;
                    }
                  }
                  onAddComment({
                    postId: post.postId,
                    chatId: myChatId,
                    authorUsername: myUserName,
                      authorDisplayName: myDisplayName,
                    text: commentReplyText,
                    parentCommentId: parentId,
                    quote: quoteTarget,
                    media: uploadedMedia,
                  });
                  setCommentReplyText("");
                  setCommentReplyMediaFiles([]);
                  setCommentReplyMediaPreview([]);
                }}
                editingCommentId={editingCommentId}
                editingCommentText={editingCommentText}
                setEditingCommentText={setEditingCommentText}
                onSaveEdit={(commentId) => {
                  if (!editingCommentText.trim()) return;
                  feedActionSocket?.editComment({ commentId, text: editingCommentText });
                  setEditingCommentId(null);
                  setEditingCommentText("");
                }}
                onCancelEdit={() => { setEditingCommentId(null); setEditingCommentText(""); }}
                onSupportComment={handleOpenCommentSupportModal}
                onViewCommentEarnings={handleOpenCommentEarningsModal}
                onOpenUser={(chatId) => setSelectedUserId(chatId)}
              />
            </React.Fragment>
          ))}
        </div>



              {/* Bottom spacer — clears the fixed bottom tab bar so the last
            post/comment's action icons (like, comment, repost, share)
            can be scrolled fully into view above it, matching X's
            behavior instead of being covered by the tab bar. */}
        <div
          style={{
            height:
              "calc(env(safe-area-inset-bottom, 0px) + var(--bottom-tabs-h, 64px) + 32px)",
          }}
          aria-hidden="true"
        />
      </div>

      {/* POST REPOST SHEET */}
      {repostMenu.open && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/40"
            onClick={() => setRepostMenu({ open: false, post: null })}
          />
          <div
            className={[
              "fixed bottom-0 left-0 right-0 z-50",
              "md:left-1/2 md:-translate-x-1/2 md:bottom-auto md:top-24 md:w-full md:max-w-xl",
              "bg-white dark:bg-neutral-900 rounded-t-2xl md:rounded-2xl",
              "shadow-2xl border-t md:border border-neutral-200 dark:border-neutral-700",
            ].join(" ")}
          >
            <RepostSheetButtons
             onRepost={() => {
  if (!onRepost) return;
  onRepost({
    postId: repostMenu.post.postId,
    chatId: myChatId,
    originalPost: repostMenu.post,
    authorUsername: myUserName,        // ✅ NEW
    authorDisplayName: myDisplayName,  // ✅ NEW
  });
  setRepostMenu({ open: false, post: null });
}}
             onQuote={() => {
  closeAllReplies();
  setQuotePost({
    type: "post",
    postId: repostMenu.post.postId,
    author: repostMenu.post.author,
    text: repostMenu.post.text,
    media: repostMenu.post.media || [],   // ✅ NEW
  });
  setShowComposer(true);
  setRepostMenu({ open: false, post: null });
}}
              onClose={() => setRepostMenu({ open: false, post: null })}
            />
          </div>
        </>
      )}

           {/* COMMENT REPOST SHEET */}
      {commentRepostMenu.open && (
        <>
          <div
            className="fixed inset-0 z-[69] bg-black/40"
            onClick={() => setCommentRepostMenu({ open: false, comment: null })}
          />
          <div
            className={[
              "fixed bottom-0 left-0 right-0 z-[70]",
              "md:left-1/2 md:-translate-x-1/2 md:bottom-auto md:top-24 md:w-full md:max-w-xl",
              "bg-white dark:bg-neutral-900 rounded-t-2xl md:rounded-2xl",
              "shadow-2xl border-t md:border border-neutral-200 dark:border-neutral-700",
            ].join(" ")}
          >
            <div className="flex justify-center py-2">
              <div className="w-10 h-1.5 rounded-full bg-neutral-300 dark:bg-neutral-700" />
            </div>
          <button
  onClick={() => {
    onRepostComment({
      commentId: commentRepostMenu.comment.commentId,
      chatId: myChatId,
      originalComment: commentRepostMenu.comment,
      authorUsername: myUserName,        // ✅ NEW
      authorDisplayName: myDisplayName,  // ✅ NEW
    });
    setCommentRepostMenu({ open: false, comment: null });
  }}
  className="w-full px-4 py-3.5 text-left hover:bg-neutral-100 dark:hover:bg-neutral-800 flex items-center gap-4 text-base font-medium touch-manipulation"
  style={{ minHeight: 52 }}
>
  🔁 Repost
</button>
           <button
  onClick={() => {
    closeAllReplies();
    setQuotePost({
      type: "comment",
      commentId: commentRepostMenu.comment.commentId,
      author: commentRepostMenu.comment.author,
      text: commentRepostMenu.comment.text,
      media: commentRepostMenu.comment.media || [],   // ✅ NEW
    });
    setShowComposer(true);
    setCommentRepostMenu({ open: false, comment: null });
  }}
  className="w-full px-4 py-3.5 text-left hover:bg-neutral-100 dark:hover:bg-neutral-800 flex items-center gap-4 text-base font-medium touch-manipulation"
  style={{ minHeight: 52 }}
>
  ✏️ Quote
</button>
            <button
              onClick={() => setCommentRepostMenu({ open: false, comment: null })}
              className="w-full px-4 py-3.5 text-left text-red-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 font-medium touch-manipulation"
              style={{ minHeight: 52 }}
            >
              Cancel
            </button>
            <div style={{ height: "env(safe-area-inset-bottom, 0px)" }} aria-hidden="true" />
          </div>
        </>
      )}

           {/* ── SUPPORT POST/COMMENT MODAL ───────────────────────────────────────── */}
      {supportModal.open && supportModal.target && (
        <div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/40"
          style={{ backdropFilter: "blur(2px)" }}
          onClick={handleCloseSupportModal}
        >
          <div
            className="bg-white dark:bg-neutral-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-sm border border-neutral-200 dark:border-neutral-700 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <span className="font-semibold text-base text-neutral-900 dark:text-neutral-100">
                {supportTarget?.kind === "comment" ? "Support this comment" : "Support this post"}
              </span>
              <button
                onClick={handleCloseSupportModal}
                disabled={supportLoading}
                className="w-8 h-8 flex items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors disabled:opacity-40"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

       <div className="px-5 pb-6">
              {supportResult ? (
                /* ── SUCCESS / RECEIPT VIEW ───────────────────────────── */
                <div className="text-center">
                  <div className="text-3xl mb-2">✅</div>
                  <div className="text-sm text-neutral-600 dark:text-neutral-300 mb-4">
                    {supportTarget?.kind === "comment"
                      ? "Your support was sent to this comment."
                      : "Your support was sent to this post."}
                  </div>
                  <div className="rounded-xl border border-neutral-200 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-700/50 divide-y divide-neutral-200 dark:divide-neutral-600 mb-4 text-sm overflow-hidden">
                    <div className="flex justify-between px-3 py-2">
                      <span className="text-neutral-500 dark:text-neutral-400">Amount</span>
                      <span className="font-medium text-neutral-800 dark:text-neutral-100">
                        {Number(supportResult.amount).toFixed(2)} USWD
                      </span>
                    </div>
                    <div className="flex justify-between px-3 py-2">
                      <span className="text-neutral-500 dark:text-neutral-400">Network fee</span>
                      <span className="font-medium text-neutral-800 dark:text-neutral-100">
                        {Number(supportResult.fee).toFixed(2)} USWD
                      </span>
                    </div>
                    <div className="flex justify-between px-3 py-2">
                      <span className="text-neutral-700 dark:text-neutral-200 font-semibold">Total</span>
                      <span className="font-semibold text-neutral-900 dark:text-neutral-50">
                        {Number(supportResult.total).toFixed(2)} USWD
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={handleCloseSupportModal}
                    className="w-full py-2.5 rounded-full bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white font-semibold text-sm transition-colors touch-manipulation"
                  >
                    Done
                  </button>
                </div>
              ) : (
                /* ── INPUT FORM ───────────────────────────────────────── */
                <>
                  {/* Target preview */}
                  <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-xl border border-neutral-200 dark:border-neutral-600 px-3 py-2.5 mb-4 text-sm text-neutral-600 dark:text-neutral-300 line-clamp-2">
                    <span className="font-medium text-neutral-800 dark:text-neutral-100">
                      @{supportTargetAuthor?.username}
                    </span>
                    {supportTargetText ? (
                      <span> {supportTargetText}</span>
                    ) : (
                      <span className="italic text-neutral-400"> (media)</span>
                    )}
                  </div>

                  {/* Amount label */}
                  <label
                    htmlFor="support-amount-input"
                    className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1.5"
                  >
                    Amount (USWD)
                  </label>

                  {/* Amount row */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-700 border border-neutral-200 dark:border-neutral-600 rounded-lg px-3 py-2 select-none whitespace-nowrap">
                      USWD
                    </span>
                    <input
                      id="support-amount-input"
                      type="number"
                      min="0.01"
                      step="0.01"
                      placeholder="0.00"
                      value={supportAmount}
                      onChange={(e) => {
                        setSupportAmount(e.target.value);
                        setSupportError("");
                      }}
                      disabled={supportLoading}
                      className="flex-1 bg-transparent border border-neutral-200 dark:border-neutral-600 focus:border-blue-400 dark:focus:border-blue-500 rounded-xl px-3 py-2 text-base outline-none focus:ring-2 focus:ring-blue-400/30 dark:focus:ring-blue-500/30 transition-colors disabled:opacity-50"
                      autoFocus
                    />
                  </div>

                  {/* Quick-select presets */}
                  <div className="flex gap-2 mb-3 flex-wrap">
                    {[1, 5, 10, 25, 50].map((v) => (
                      <button
                        key={v}
                        onClick={() => { setSupportAmount(String(v)); setSupportError(""); }}
                        disabled={supportLoading}
                        className="text-xs px-3 py-1 rounded-full border border-neutral-200 dark:border-neutral-600 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors disabled:opacity-40 touch-manipulation"
                      >
                        {v}
                      </button>
                    ))}
                  </div>

                  {/* Amount / fee / total preview */}
                  {parseFloat(supportAmount) > 0 && (
                    <div className="rounded-xl border border-neutral-200 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-700/50 divide-y divide-neutral-200 dark:divide-neutral-600 mb-3 text-sm overflow-hidden">
                      <div className="flex justify-between px-3 py-2">
                        <span className="text-neutral-500 dark:text-neutral-400">Amount</span>
                        <span className="font-medium text-neutral-800 dark:text-neutral-100">
                          {parseFloat(supportAmount).toFixed(2)} USWD
                        </span>
                      </div>
                      <div className="flex justify-between px-3 py-2">
                        <span className="text-neutral-500 dark:text-neutral-400">Network fee</span>
                        <span className="font-medium text-neutral-800 dark:text-neutral-100">
                          {NETWORK_FEE_USWD.toFixed(2)} USWD
                        </span>
                      </div>
                      <div className="flex justify-between px-3 py-2">
                        <span className="text-neutral-700 dark:text-neutral-200 font-semibold">Total</span>
                        <span className="font-semibold text-neutral-900 dark:text-neutral-50">
                          {(parseFloat(supportAmount) + NETWORK_FEE_USWD).toFixed(2)} USWD
                        </span>
                      </div>
                    </div>
                  )}


                  {/* Error message */}
                  {supportError && (
                    <p className="text-xs text-red-500 dark:text-red-400 mb-3 -mt-1">
                      {supportError}
                    </p>
                  )}

                  {/* Submit button */}
                  <button
                    onClick={handleSupportSubmit}
                    disabled={supportLoading || !supportAmount || parseFloat(supportAmount) <= 0}
                    className="w-full py-2.5 rounded-full bg-blue-500 hover:bg-blue-600 active:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm transition-colors touch-manipulation"
                  >
                    {supportLoading ? "Sending…" : "Send support"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

         {/* ── EARNINGS MODAL (post/comment author — read-only) ────────────────── */}
      {earningsModal.open && earningsModal.target && (
        <div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/40"
          style={{ backdropFilter: "blur(2px)" }}
          onClick={handleCloseEarningsModal}
        >
          <div
            className="bg-white dark:bg-neutral-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-sm border border-neutral-200 dark:border-neutral-700 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <span className="font-semibold text-base text-neutral-900 dark:text-neutral-100">
                {earningsTarget?.kind === "comment" ? "Comment earnings" : "Post earnings"}
              </span>
              <button
                onClick={handleCloseEarningsModal}
                className="w-8 h-8 flex items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="px-5 pb-6">
              {/* Target preview */}
              <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-xl border border-neutral-200 dark:border-neutral-600 px-3 py-2.5 mb-4 text-sm text-neutral-600 dark:text-neutral-300 line-clamp-2">
                {earningsTargetText ? (
                  <span>{earningsTargetText}</span>
                ) : (
                  <span className="italic text-neutral-400">(media)</span>
                )}
              </div>

              {/* Received amount */}
              <div>
                <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                  {earningsTarget?.kind === "comment" ? "Received for this comment" : "Received for this post"}
                </div>
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-neutral-200 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-700/50">
                  <span className="text-lg font-semibold text-neutral-800 dark:text-neutral-100">
                    {Number(earningsAmount).toFixed(2)}
                  </span>
                  <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">USWD</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <UserInfoOverlay
  open={!!selectedUserId}
  onClose={() => setSelectedUserId(null)}
  user={users.find((u) => String(u.chatId) === String(selectedUserId))}
  mutualGroups={[]}
  uploadImage={uploadFeedMedia}
  socket={feedSocket}
  myChatId={myChatId}
  isFollowing={
    users.find((u) => String(u.chatId) === String(selectedUserId))?.isFollowing
  }
  onFollow={() => feedSocket?.followUser?.(selectedUserId)}
  onUnfollow={() => feedSocket?.unfollowUser?.(selectedUserId)}
  followersCount={
    users.find((u) => String(u.chatId) === String(selectedUserId))
      ?.followersCount || 0
  }
  followingCount={
    users.find((u) => String(u.chatId) === String(selectedUserId))
      ?.followingCount || 0
  }
/>

      {bottomSheet}
      {desktopComposer}
      {mobileComposer}
    </div>
  );
}